import { useEffect, useState } from "react";

import { useTask } from "../../../hooks/query";
import { mapTaskAssetToImage, TaskListAsset } from "../../../utils/taskAssetImages";
import { ImageWithMetadata } from "../../../constants/inspectionPhotos";
import { AlbumGroup, AlbumSectionDefinition } from "../types";
import { offlineStore, OfflinePhoto } from "../../../utils/offlineStore";

interface UseAlbumGroupsOptions {
    sections: AlbumSectionDefinition[];
    taskId: string;
    assetType: string;
}

function buildGroups(sections: AlbumSectionDefinition[], assets: TaskListAsset[]): AlbumGroup[] {
    return sections.map((section) => ({
        ...section,
        images: assets
            .filter((asset) => asset.Label === section.label)
            .map((asset) => mapTaskAssetToImage(asset)),
    }));
}

export function useAlbumGroups(props: UseAlbumGroupsOptions) {
    const { sections, taskId, assetType } = props;
    const { useListAssets } = useTask();
    const listAssetsSvc = useListAssets();
    const [groups, setGroups] = useState<AlbumGroup[]>(() =>
        sections.map((section) => ({ ...section, images: [] })),
    );
    const [loading, setLoading] = useState(true);

    const fetchGroups = async () => {
        setLoading(true);
        let backendAssets: TaskListAsset[] = [];
        
        try {
            if (taskId && !taskId.startsWith("offline-") && navigator.onLine) {
                backendAssets = await listAssetsSvc.mutateAsync({
                    TaskId: taskId,
                    AssetType: assetType,
                });
            }
        } catch (error) {
            console.error("Failed to fetch album assets", error);
        }
        
        // Also get offline photos
        let offlinePhotos: OfflinePhoto[] = [];
        try {
            offlinePhotos = await offlineStore.getOfflinePhotos();
        } catch (e) {
            console.error("Failed to fetch offline photos", e);
        }
        
        const myOfflinePhotos = offlinePhotos.filter(p => p.taskId === taskId || p.projectId === taskId); 
        // Note: ProjectId might be passed as taskId in some places, so we check both.

        const mergedGroups = sections.map((section) => {
            const serverImages = backendAssets
                .filter((asset) => asset.Label === section.label)
                .map(mapTaskAssetToImage);
                
            const offlineImages = myOfflinePhotos
                .filter(p => p.label === section.label)
                .map(p => ({
                    url: URL.createObjectURL(p.file),
                    assetId: p.offlineId,
                    note: p.note || null,
                } as ImageWithMetadata));
                
            return {
                ...section,
                images: [...serverImages, ...offlineImages]
            };
        });
        
        setGroups(mergedGroups);
        setLoading(false);
    };

    useEffect(() => {
        fetchGroups();
    }, [taskId, assetType]);

    const updateGroupImages = (label: string, images: ImageWithMetadata[]) => {
        setGroups((prev) =>
            prev.map((group) => (group.label === label ? { ...group, images } : group)),
        );
    };

    return {
        groups,
        loading,
        refreshGroups: fetchGroups,
        updateGroupImages,
    };
}
